import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { legacyRouteToReportQuery, LegacyWalletScreen } from 'src/app/pages/wallet/reports/report-query';
import { AuthService } from './auth.service';
import { ADMINISTRATOR } from './user.service';
import { WalletReportsService } from './wallet-reports.service';

/**
 * Redireciona as rotas antigas (Mensagens/Respostas/Recarga) para Minha Carteira › Relatórios
 * quando o cliente está MIGRADO (M7, task 17tm466nz8b). O contrato (`~/trabalhos/m7-coord.md`,
 * seção 1) é explícito: "redirecionamento das rotas antigas é responsabilidade do front" — as
 * rotas usam `useHash: true`, o servidor nunca vê o fragmento, então não há como o back
 * redirecionar por HTTP.
 *
 * Chama `navigation()` direto (não lê o snapshot cacheado do `WalletNavigationService`) de
 * propósito: é uma decisão de UMA vez por navegação, e o cache pode não ter resolvido ainda
 * se o cliente abriu a URL antiga direto (link salvo, histórico) antes do menu carregar.
 *
 * `legacyWalletScreen` vem de `route.data` (ver `pages-routing.module.ts`) — um guard só,
 * reaproveitado nas 5 rotas antigas, cada uma dizendo qual tela é.
 */
@Injectable({ providedIn: 'root' })
export class LegacyWalletRedirectGuard implements CanActivate {

  constructor(
    private reportsService: WalletReportsService,
    private authService: AuthService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const originalUser = this.authService.getUser(true);

    // Admin ORIGINAL só escapa FORA de simulação: é quem precisa das telas antigas para
    // atender qualquer cliente, migrado ou não. Simulando um cliente migrado, o admin é
    // redirecionado igual ao cliente (decisão do Bruno, 14/10) — mesmo critério do
    // `MenuService`. `.simulated` só existe no usuário EFETIVO (`getUser()` sem forçar
    // original), nunca no original devolvido por `getUser(true)`.
    const effectiveUser = this.authService.getUser();
    if (originalUser && originalUser.profile_id === ADMINISTRATOR && !(effectiveUser && effectiveUser.simulated)) {
      return of(true);
    }

    const screen = route.data.legacyWalletScreen as LegacyWalletScreen | 'recharges-new';

    return this.reportsService.navigation().pipe(
      map((navigation) => {
        if (screen === 'recharges-new') {
          return navigation.hide.recharges ? this.router.createUrlTree(['/wallet'], { queryParams: { recarga: '1' } }) : true;
        }

        const hidden = screen === 'messages'
          ? navigation.hide.messages_report
          : screen === 'responses'
            ? navigation.hide.lead_responses
            : navigation.hide.recharges;

        if (!hidden) { return true; }

        const query = legacyRouteToReportQuery(screen, {
          month: route.queryParamMap.get('month') || undefined,
          year: route.queryParamMap.get('year') || undefined,
          date: route.paramMap.get('date') || undefined,
        });

        return this.router.createUrlTree(['/wallet/relatorios'], { queryParams: query });
      }),
      // Sem resposta (rede fora), não bloqueia a navegação nem some com a tela antiga: uma
      // falha aqui não pode ser pior do que a decisão de hoje (sem redirecionamento nenhum).
      catchError(() => of(true)),
    );
  }
}
